import { useEffect, useRef, useState } from 'react'

import _ from 'lodash'
import { DateTime, Interval } from 'luxon'
import { useRecoilValue } from 'recoil'
import { ItList, ItListItem } from 'types/graphql'

import { useLocation, useParams } from '@redwoodjs/router'

import { currentItemIdState } from 'src/atoms/ui.atom'
import { toast } from 'src/components/widgets/Toaster'
import { LOCAL_STORAGE_AFFILIATED_ITLIST_KEY } from 'src/lib/constants'
import { useCurrentUser } from 'src/lib/hooks/auth/useCurrentUser'
import {
  isViewedUnlock,
  saveLocalViewUnlock,
} from 'src/lib/hooks/mutations/recentlyViewed/localViewUnlock'
import { useSaveView } from 'src/lib/hooks/mutations/recentlyViewed/useSaveView'
import usePurchaseItList from 'src/lib/hooks/stripe/usePurchaseItList'
import { set } from 'src/lib/storedAffiliations'
import { isBasicInfoItem, isNumber } from 'src/lib/typeGuards'
import { getBrandColor } from 'src/lib/utils'
import { BasicInfoItem } from 'src/types'

import ItemCard from './Sections/ItemCard/ItemCard'
import NoItemsEmptyState from './Sections/NoItemsEmptyState/NoItemsEmptyState'

import { BuyOptions, DetailsElementProps, TrackingData } from '.'

const CONFETTI_TIMEOUT_IN_SEC = 15

export default function useItListDetails(
  locked: boolean,
  itList: ItList,
  items: ItListItem[],
  isPreview?: boolean
) {
  const { a: affiliateId } = useParams()
  const { pathname, hash } = useLocation()
  const currentItemId = useRecoilValue(currentItemIdState)
  const {
    id: currentUserId,
    isAuthenticated,
    stripeOnboardingStatus,
  } = useCurrentUser()
  const { initPurchase, loading } = usePurchaseItList()
  const saveView = useSaveView()
  const [showConfettis, setShowConfettis] = useState(false)
  const emailElRef = useRef<HTMLInputElement>()
  const withEmailPaywall =
    !itList.price && itList.unlockWithEmail && !isAuthenticated
  const containerRef = useRef<HTMLDivElement>()
  const jumpToItemById = (itemId: number | BasicInfoItem) => {
    jumpToItem(`#it-list-item-${itemId}`)
  }
  const jumpToItem = (itemIdAttr: string) => {
    setTimeout(() => {
      containerRef.current
        .querySelector(itemIdAttr)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 150)
  }
  useEffect(() => {
    if (!locked) {
      if (shouldShowConfettis(itList, !!currentUserId)) {
        setShowConfettis(true)
      }
      saveLocalViewUnlock(itList.id)
    }
  }, [currentUserId, itList, locked])
  useEffect(() => {
    if (isPreview) return
    saveView({ ...itList, itListId: itList.id })
    if (
      itList.relationship === 'affiliate' &&
      stripeOnboardingStatus === 'ready'
    ) {
      history.replaceState(null, '', `${pathname}?a=${itList.affiliateId}`)
    } else if (affiliateId) {
      set(`${LOCAL_STORAGE_AFFILIATED_ITLIST_KEY}:${itList.id}`, +affiliateId)
      history.replaceState(null, '', pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    // In case of search (this is also used when clicking on items in the editor)
    // There is no way to listen to hash change coming from replaceState or pushState
    // And listening to hash change reset the scroll of the page, so we use a state
    // variable to detect when the hash has been changed.
    // Even if there was a way to listen if the user does:
    // click result X > scroll manually > click result X again
    // it would not scroll the second time, because the hash has not changed
    if (
      (isNumber(currentItemId) && !locked) ||
      (isBasicInfoItem(currentItemId) && locked)
    ) {
      jumpToItemById(currentItemId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItemId, locked])
  useEffect(() => {
    // This only scrolls to the item on page load
    hash && !locked && jumpToItem(hash)
  }, [hash, locked])

  if (!itList) return null
  const trackingData: TrackingData = {
    isOwner: itList.relationship === 'owner',
    owningCreatorEmail: itList.creator.email,
    owningCreatorId: `${itList.creator.id}`,
    owningCreatorUsername: itList.creator.username,
    productId: `${itList.liveVersion?.id || itList.id}`,
  }

  const brandColor = getBrandColor(itList?.creator)
  const buy = async (options?: BuyOptions) => {
    const eventName = options?.eventName || 'Buy Now Clicked'
    const forceCheckout = options?.forceCheckout || false
    if (isPreview) {
      toast('info', 'Purchase action not available in preview')
      return
    }
    if (withEmailPaywall) {
      emailElRef.current?.focus()
    } else {
      initPurchase({
        eventName,
        itList,
        forceCheckout,
      })
    }
  }

  const detailsElementProps: DetailsElementProps = {
    locked,
    itList,
    isPreview,
    trackingData,
    emailElRef,
    buy,
    buyIsLoading: loading,
    brandColor,
    withEmailPaywall,
    currentItemId,
    itListItems: items,
  }

  const itemsAsItemDetails = locked
    ? []
    : items.length
    ? items.map((item) => ({ item, Element: ItemCard }))
    : [{ Element: NoItemsEmptyState }]

  return {
    showConfettis,
    brandColor,
    jumpToItemById,
    containerRef,
    detailsElementProps,
    itemsAsItemDetails,
  }
}

function shouldShowConfettis(itList: ItList, isAuthenticated: boolean) {
  if (isAuthenticated) {
    return (
      itList.purchaseDate &&
      itList.relationship === null &&
      Interval.fromDateTimes(
        DateTime.fromISO(itList.purchaseDate),
        DateTime.local()
      ).toDuration('second').seconds < CONFETTI_TIMEOUT_IN_SEC
    )
  }

  return !isViewedUnlock(itList.id)
}
